import { WatchQueryFetchPolicy } from '@apollo/client';
import { useState } from 'react';

import { TaskSection } from '@components/TaskList/TaskListTable';
import {
  TaskSortOption,
  TaskGroup,
  TaskStatus,
  TaskPriority,
  Scalars,
} from '@graphql/generated';
import { useListTasksFromQuery } from '@hooks/useListTasksFromQuery';

import type { ProjectUpdatedRefreshMode } from '@hooks/useRefreshOnProjectUpdated';

export type TaskSearchParam = {
  fetchPolicy?: WatchQueryFetchPolicy;
  skip?: boolean;
  projectIds?: string[];
  priority?: TaskPriority[];
  dueDateBegin?: Scalars['ISO8601DateTime'];
  dueDateEnd?: Scalars['ISO8601DateTime'];
  memberIds?: string[];
  /** TA-10460 follow-up — see useListTasksFromQuery's pollInterval doc.
   * Spread straight through to each section's underlying query below. */
  pollInterval?: number;
  /** TA-11139 — see useRefreshOnProjectUpdated. Spread through like
   * pollInterval. */
  refreshOnProjectUpdated?: ProjectUpdatedRefreshMode;
};

type BuildTaskSectionOptions = {
  param: TaskSearchParam;
  sortBy?: TaskSortOption;
  queryExtra?: Partial<Parameters<typeof useListTasksFromQuery>[0]>;
  sectionId: string;
  sectionName: string;
};

const useBuildTaskSection = ({
  param,
  sortBy,
  queryExtra,
  sectionId,
  sectionName,
}: BuildTaskSectionOptions) => {
  const TASK_PAGE_SIZE = 25;

  const {
    tasks,
    fetchFromCursorAsync,
    pageInfo,
    refetch,
    refreshing,
    loading,
  } = useListTasksFromQuery({
    first: TASK_PAGE_SIZE,
    ...param,
    ...queryExtra,
    ...(sortBy ? { sortBy } : {}),
    // TA-10472: listTasks is cached per filter combination (projectIds is a
    // keyArg), so every project filter has its own cache entry. Task
    // mutations only `refetchQueries: ['listTasks']`, which refreshes the
    // *mounted* queries — a filter the user isn't currently looking at keeps
    // its stale, already-complete cache entry. Under cache-first, selecting
    // that filter later replays the stale entry without ever hitting the
    // network, so tasks created since it was cached are silently missing
    // (only the most recently created ones, which is exactly the symptom).
    // cache-and-network still paints the cached rows instantly, then
    // reconciles from the server. Matches ProjectTasks.web.tsx, which already
    // uses cache-and-network for the same query and does not show this bug.
    fetchPolicy: param.fetchPolicy ?? 'cache-and-network',
  });

  const [loadingMore, setLoadingMore] = useState(false);

  const fetchMoreAsync = async () => {
    if (!pageInfo?.hasNextPage || loadingMore) return;

    setLoadingMore(true);
    try {
      await fetchFromCursorAsync();
    } finally {
      setLoadingMore(false);
    }
  };

  const taskSection: TaskSection & {
    refetch: () => Promise<unknown>;
    refreshing: boolean;
    loading: boolean;
    loadingMore: boolean;
  } = {
    sectionData: {
      sectionHeader: {
        id: sectionId,
        name: sectionName,
        count: tasks.length,
      },
      data: tasks,
      hasNextPage: pageInfo?.hasNextPage,
    },
    loadMore: fetchMoreAsync,
    refetch,
    refreshing,
    loading,
    loadingMore,
  };

  return taskSection;
};

export const useTaskSections = (
  param: TaskSearchParam,
  sortBy?: TaskSortOption,
  taskCollaborators?: boolean
) => {
  const current = useBuildTaskSection({
    param,
    sortBy,
    sectionId: TaskGroup.Current,
    sectionName: 'Current',
    queryExtra: {
      dueGroup: TaskGroup.Current,
      excludeCompleted: true,
      taskCollaborators,
    },
  });

  const overdue = useBuildTaskSection({
    param,
    sortBy: sortBy ?? TaskSortOption.DueDateAsc,
    sectionId: TaskGroup.Overdue,
    sectionName: 'Overdue',
    queryExtra: {
      dueGroup: TaskGroup.Overdue,
      excludeCompleted: true,
      taskCollaborators,
    },
  });

  const completed = useBuildTaskSection({
    param,
    sortBy: sortBy ?? TaskSortOption.DueDateDesc,
    sectionId: 'COMPLETED',
    sectionName: 'Completed',
    queryExtra: {
      status: TaskStatus.Completed,
      taskCollaborators,
    },
  });

  return {
    groupedCurrentTasks: current,
    groupedOverDueTasks: overdue,
    groupedCompletedTasks: completed,
  };
};
